import { useState, useEffect } from "react";
import { Search, Tag } from "lucide-react";
import { V1BEClient } from "@/clients";
import { IOrganizationEvent } from "@/types/EventTypes";
import PageHeader from "../ui/PageHeader";
import EventCard from "./EventCard";

const Events = () => {
  const [events, setEvents] = useState<IOrganizationEvent[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState("date");

  useEffect(() => {
    const load = async () => {
      setIsLoading(true);
      try {
        const data = await V1BEClient.getOrganizationEvents();
        setEvents(data);
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, []);

  const filteredEvents = events
    .filter((event) => {
      const term = searchTerm.toLowerCase();
      const matchesSearch =
        event.name.toLowerCase().includes(term) ||
        (event.description || "").toLowerCase().includes(term);
      return matchesSearch;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case "date": {
          const aDate = a.date_from ? new Date(a.date_from).getTime() : 0;
          const bDate = b.date_from ? new Date(b.date_from).getTime() : 0;
          return aDate - bDate;
        }
        case "name":
          return a.name.localeCompare(b.name);
        default:
          return 0;
      }
    });

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 py-12">
        <div className="container mx-auto px-4">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading exhibits...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="container mx-auto px-4">
        {/* Header */}
        <PageHeader
          title="Wedding Supplier Exhibits"
          subtitle="Discover upcoming exhibits where wedding suppliers showcase their finest work. 
            Meet vendors, view portfolios, and book services for your special day."
        />

        {/* Search and Sort */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Search */}
            <div className="relative">
              <Search
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                size={20}
              />
              <input
                type="text"
                placeholder="Search exhibits..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors duration-200"
              />
            </div>

            {/* Sort */}
            <div className="relative">
              <Tag
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                size={20}
              />
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors duration-200 appearance-none bg-white"
              >
                <option value="date">Sort by Date</option>
                <option value="name">Sort by Name</option>
              </select>
            </div>
          </div>
        </div>

        {/* Results Count */}
        <div className="mb-6">
          <p className="text-gray-600">
            Showing {filteredEvents.length} exhibit
            {filteredEvents.length !== 1 ? "s" : ""}
            {searchTerm && ` for "${searchTerm}"`}
          </p>
        </div>

        {/* Events Grid */}
        {filteredEvents.length > 0 ? (
          <div className="columns-1 md:columns-2 lg:columns-3 gap-8 space-y-8">
            {filteredEvents.map((event) => (
              <EventCard key={event.id} event={event} />
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <div className="text-gray-400 mb-4">
              <Search size={64} className="mx-auto" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              No exhibits found
            </h3>
            <p className="text-gray-600">
              Try adjusting your search criteria or check back later for new
              exhibits.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Events;
